<!-- SearchTablerLayout.vue -->
<template>
  <div class="py-4 container-fluid">
    <div class="row g-4 align-items-start">
      <!-- 좌측: Tabler 스타일 필터 -->
      <div class="col-12 col-xl-3">
        <aside class="card shadow-sm filters-sticky">
          <div class="card-body pb-2">
            <h6 class="mb-1" style="font-weight: 800;">검색조건</h6>
            <div class="text-secondary text-xs mb-4" v-if="showStats">
              총 {{ prettyNumber(totalCnt) }} 건이 검색되었습니다. ({{ elapsed }} seconds)
            </div>
            <div v-else class="text-secondary text-xs mb-4">
              이벤트를 필터링하여 조회가능합니다.
            </div>

            <div class="mb-3 filter-box">
              <div class="filter-box-label">식별정보</div>

              <div class="my-1 d-grid gap-2">
                <input class="form-control form-control-sm" placeholder="EPC (Electronic Product Code)"
                      v-model.trim="form.epc">
                <input class="form-control form-control-sm" placeholder="GTIN (Global Trade Item Number)"
                      v-model.trim="form.gtin">
                <input class="form-control form-control-sm" placeholder="Serial"
                      v-model.trim="form.serial">
              </div>
            </div>

            <div class="mb-3 filter-box">
              <label class="filter-box-label d-block mb-2 ms-0">공정 단계</label>
              <select
                class="form-select form-select-sm"
                v-model="form.category"
                @change="selectCategory(form.category)"
              >
                <option :value="''" >선택하세요</option>
                <option :value="'-'" disabled>------------------------------------</option>
                <option v-for="c in categories" :key="c.value" :value="c.value">
                  {{ c.label }}
                </option>
              </select>
            </div>

            <div class="mb-3 filter-box">
              <div class="filter-box-label">시간설정</div>

              <div class="mt-1 d-flex flex-wrap gap-2">
                <button type="button"
                        class="btn btn-outline-secondary btn-sm px-3 mb-2"
                        :class="{ active: activePreset === 'today' }"
                        @click="quickRange('today')">오늘</button>

                <button type="button"
                        class="btn btn-outline-secondary btn-sm px-3 mb-2"
                        :class="{ active: activePreset === '7d' }"
                        @click="quickRange('7d')">최근 7일</button>

                <button type="button"
                        class="btn btn-outline-secondary btn-sm px-3 mb-2"
                        :class="{ active: activePreset === '30d' }"
                        @click="quickRange('30d')">최근 30일</button>
              </div>

              <div class="rowg-2">
                <div class="col-12 mb-1">
                  <label for="timeFrom" class="section-label text-xs mb-1 ms-0">검색시작</label>
                  <input id="timeFrom" type="datetime-local"
                        class="form-control form-control-sm"
                        v-model="form.timeFrom">
                </div>
                <div class="col-12">
                  <label for="timeTo" class="section-label text-xs mb-1 ms-0">검색종료</label>
                  <input id="timeTo" type="datetime-local"
                        class="form-control form-control-sm"
                        v-model="form.timeTo">
                </div>
              </div>
            </div>

            <!-- ACTIONS -->
            <div class="row pt-1 g-2">
              <div class="col-4">
                <button class="btn btn-outline-secondary w-100" type="button" @click="resetForm">초기화</button>
              </div>
              <div class="col-8">
                <button class="btn btn-success w-100" type="button" @click="apply">검색</button>
              </div>
            </div>

            <div class="row pt-2 pb-4 g-2">
              <div class="col-12 d-flex gap-2">
                <button
                  class="btn btn-outline-secondary w-100"
                  type="button"
                  @click="toggleAdvanced"
                  :aria-expanded="showAdvanced"
                  :aria-controls="'adv-filters'"
                >
                  <i class="me-1" :class="showAdvanced ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                  상세검색
                  <span v-if="advancedCount" class="badge bg-secondary ms-1">{{ advancedCount }}</span>
                </button>
              </div>
            </div>

            <transition name="collapse">
              <div v-show="showAdvanced" id="adv-filters">
                <div class="mb-3 filter-box">
                  <div class="filter-box-label">이벤트타입</div>
                  <div class="mt-2 d-flex flex-column gap-2">
                    <label v-for="e in eventTypes" :key="e.value" class="form-check">
                      <input class="form-check-input" type="checkbox"
                            :value="e.value" v-model="form.eventTypes">
                      <span class="form-check-label text-capitalize">{{ e.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="mb-3 filter-box">
                  <div class="filter-box-label">액션</div>
                  <div class="mt-2 d-flex flex-column gap-2">
                    <label v-for="a in actions" :key="a.value" class="form-check">
                      <input class="form-check-input" type="checkbox"
                            :value="a.value" v-model="form.actions">
                      <span class="form-check-label text-capitalize">{{ a.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="mb-3 filter-box">
                  <div class="filter-box-label">업무단계</div>
                  <div class="mt-2 d-flex flex-column gap-2">
                    <label v-for="b in bizSteps" :key="b.value" class="form-check">
                      <input class="form-check-input" type="checkbox"
                            :value="b.value" v-model="form.bizSteps">
                      <span class="form-check-label text-capitalize">{{ b.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="mb-3 filter-box">
                  <div class="filter-box-label">처리상태</div>
                  <div class="mt-2 d-flex flex-column gap-2">
                    <label v-for="d in dispositions" :key="d.value" class="form-check">
                      <input class="form-check-input" type="checkbox"
                            :value="d.value" v-model="form.dispositions">
                      <span class="form-check-label text-capitalize">{{ d.label }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </transition>

          </div>
        </aside>
      </div>

      <!-- 우측: 결과 영역(비워둠) -->
      <div class="col-12 col-xl-9">
        <RouterView v-slot="{ Component, route }">
          <Transition name="slide-fade" mode="out-in" appear>
            <KeepAlive include="EventTable">
            <component
              :is="Component"
              :key="route.meta.keepAlive ? route.name : route.fullPath"
              v-bind="route.name === 'EventList' ? { filters: form, applyTick } : {}"
              @update-stats="onUpdateStats"
              @row-click="onRowClick"
            />
            </KeepAlive>
          </Transition>
        </RouterView>
    </div>
    </div>
  </div>
</template>

<script>
import { SEARCH_TABLER_OPTS } from '@/constants/searchTablerOpts.js';
import { CATEGORY_PRESETS } from '@/constants/categoryPresets.js';

export default {
  name: 'EventSearch',
  components: {  },
  data() {
    return {
      ...SEARCH_TABLER_OPTS,
      totalCnt: 0,
      elapsed: 0,
      showAdvanced: false,
      activePreset: '',
      applyTick: 0,
      showStats: false,
      form: {
        category: '',
        eventTypes: [],
        actions: [],
        bizSteps: [],
        dispositions: [],
        timeFrom: '',
        timeTo: ''
      },
    };
  },
  computed: {
    advancedCount() {
      const { eventTypes, actions, bizSteps, dispositions } = this.form;
      return (eventTypes?.length || 0)
          + (actions?.length || 0)
          + (bizSteps?.length || 0)
          + (dispositions?.length || 0);
    }
  },
  mounted() {
    this.form = { ...this.form };
    if (!this.form.timeFrom && !this.form.timeTo) {
      this.quickRange('today');
    }
    if (this.$route.name === 'EventList') {
      this.autoApply();
    }
  },
  methods: {
    async ensureListRoute() {
      if (this.$route.name !== 'EventList') {
        await this.$router.push({ name: 'EventList' }).catch(() => {});
        await new Promise(r => this.$nextTick(r));
      }
    },
    async autoApply() {
      if (this.$route.name === 'EventList') {
        this.applyTick++;
      }
    },
    async apply() {
      await this.ensureListRoute();
      this.applyTick++; 
    },
    toggleAdvanced() {
      this.showAdvanced = !this.showAdvanced;
    },
    resetForm() {
      this.form = {
        category: '',
        eventTypes: [],
        actions: [],
        bizSteps: [],
        dispositions: [],
        timeFrom: '',
        timeTo: ''
      };
      this.quickRange('today');
    },
    quickRange(preset) {
      const now = new Date();
      const toISO = d => new Date(d.getTime()-d.getTimezoneOffset()*60000).toISOString().slice(0,16);
      let from = new Date();
      if (preset === 'today') {
        from.setHours(0,0,0,0);
      } else if (preset === '7d') {
        from = new Date(now.getTime() - 7*24*3600*1000);
      } else if (preset === '30d') {
        from = new Date(now.getTime() - 30*24*3600*1000);
      }
      this.form.timeFrom = toISO(from);
      this.form.timeTo   = toISO(now);
      this.activePreset = preset;
    },
    applyCategoryPreset(category) {
      // 상세검색 필드 전체 리셋
      this.form.eventTypes   = [];
      this.form.actions      = [];
      this.form.bizSteps     = [];
      this.form.dispositions = [];

      const preset = CATEGORY_PRESETS[category];
      if (!preset) return; // 매핑 없는 경우(예: '선택하세요')는 비운 상태 유지

      // 프리셋 적용
      this.form.eventTypes   = [...preset.eventTypes];
      this.form.actions      = [...preset.actions];
      this.form.bizSteps     = [...preset.bizSteps];
      this.form.dispositions = [...preset.dispositions];
    },
    selectCategory(val) {
      this.form.category = val;
      this.applyCategoryPreset(val);
    },
    onUpdateStats({ total, elapsed }) {
      this.totalCnt = total;
      this.elapsed  = elapsed;
      this.showStats = true;
    },
    onRowClick(item) {
      const id = item?.eventID;
      if (!id) return;
      this.$router.push({ path: `/eventList/detail/${encodeURIComponent(id)}` });
    },
  }
};
</script>

<style scoped>
/* 상단 네비가 고정이면 top 값을 조정 */
.filters-sticky {
  position: sticky;
  top: var(--sticky-top, 88px);
  border-radius: 1rem;
}

/* 섹션 타이틀: Tabler 느낌 */
.section-label {
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .06em;
  color: var(--bs-secondary);
}

/* 카테고리 메뉴 */
.menu-list { margin: 0; padding: 0; }
.menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: .75rem;
  justify-content: space-between;
  padding: .57rem .75rem;
  border: 1px solid transparent;
  background: transparent;
  border-radius: .5rem;
  transition: background-color .15s ease, border-color .15s ease;
  font-weight: 500;
}
.menu-item:hover { background: rgba(0,0,0,.03); }
.menu-item.active {
  background: #f5f7fb; /* 밝은 하이라이트 */
  border-color: rgba(0,0,0,.06);
}
.menu-item .count { font-weight: 600; }

/* 라디오/체크 스타일 미세 보정 */
.form-check-input {
  width: 1rem; height: 1rem; margin-top: .125rem;
}
.form-check-label { user-select: none; }

.text-truncate { font-size: 14px; }

.filter-box {
  position: relative;
  border: 1px solid rgba(0,0,0,.125);
  border-radius: .5rem;
  padding: 1rem .75rem .75rem; /* 위쪽 패딩 조금 늘려줌 */
}

.filter-box-label {
  position: absolute;
  top: -0.6rem;        /* 박스 위로 살짝 올라오게 */
  left: 0.75rem;
  background: #fff;    /* 카드 바탕색과 같게 */
  padding: 0 .5rem;
  font-size: .8rem;
  font-weight: 700;
  color: var(--bs-secondary);
}

.collapse-enter-active,
.collapse-leave-active {
  transition: height .22s ease, opacity .22s ease;
  overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
  height: 0;
  opacity: 0;
}
.collapse-enter-to,
.collapse-leave-from {
  height: auto;
  opacity: 1;
}
.btn-outline-secondary.active {
  background-color: #2563eb !important;
  border-color: #4c98ca !important;
  color: #fff !important;
}

/* 슬라이드+페이드 */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity .22s ease, transform .22s ease;
}
.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(16px);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}

/* 모션 최소화 환경 배려 (선택) */
@media (prefers-reduced-motion: reduce) {
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }
}
</style>
